<script>
  import { navigate } from '../../lib/stores/router.svelte.js';
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import { getBinderColor } from '../../lib/logic/utils.js';
  import { buildBinderUrl } from '../../lib/logic/search-logic.js';
  import { onMount } from 'svelte';
  import NotSharedHere from '../shared/NotSharedHere.svelte';
  import SetSymbol from '../shared/SetSymbol.svelte';

  let { printing, currencySymbol = '$', onClose } = $props();

  const imageUrl = $derived(getCardImageUrl(printing.imageData, 'large'));
  const setCode = $derived((printing.set || '').toUpperCase());
  const rarity = $derived((printing.rarity || 'common').toLowerCase());
  const priceDisplay = $derived(
    printing.price > 0 ? `${currencySymbol}${printing.price.toFixed(2)}` : null
  );
  // Group search lists a binder once per Claim (ADR-0021 decision 6), so two
  // Claimants of one binder are two rows but still one binder.
  const binderCount = $derived(new Set((printing.binders || []).map((b) => b.binderKey)).size);
  const finishLabel = $derived(
    printing.finish === 'foil' || printing.isFoil
      ? 'Foil'
      : printing.finish === 'etched'
        ? 'Etched'
        : null
  );

  // Deliberately NOT async: Svelte only honors a cleanup function returned
  // synchronously from onMount. An async callback returns a Promise, so the
  // Escape listener and body scroll lock below would never be undone.
  onMount(() => {
    /** @param {KeyboardEvent} e */
    function handleKeydown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeydown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = '';
    };
  });

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  function handleBinderClick(e, binder) {
    e.preventDefault();
    const url = buildBinderUrl(binder.source, binder.sourceType, binder.sourceId);
    if (url) {
      onClose();
      navigate('binder', { binderUrl: url, shortPath: url });
    }
  }
</script>

<!-- Backdrop: click-outside closes; keyboard close is the document Escape listener in onMount -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div class="detail-backdrop" onclick={handleBackdropClick}>
  <div class="detail-content">
    <button class="detail-close" onclick={onClose} aria-label="Close">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>

    <div class="detail-layout">
      <!-- Card Image -->
      <div class="detail-image-area">
        {#if imageUrl}
          <img class="detail-card-image" src={imageUrl} alt={printing.name} />
        {/if}
      </div>

      <!-- Card Info -->
      <div class="detail-info">
        <h2 class="detail-card-name">{printing.name}</h2>

        <div class="detail-meta">
          <span class="detail-set rarity-{rarity}">
            {#if printing.set}<SetSymbol code={printing.set} {rarity} />{/if}
            {setCode}
          </span>
          <span class="detail-collector">#{printing.collectorNumber || ''}</span>
          {#if finishLabel}
            <span class="detail-finish-badge" class:foil={finishLabel === 'Foil'}
              >{finishLabel}</span
            >
          {/if}
        </div>

        {#if printing.typeLine}
          <p class="detail-type-line">{printing.typeLine}</p>
        {/if}

        <div class="detail-stats">
          {#if priceDisplay}
            <div class="detail-stat">
              <span class="detail-stat-label">Price</span>
              <span class="detail-stat-value">{priceDisplay}</span>
            </div>
          {/if}
          <div class="detail-stat">
            <span class="detail-stat-label">Total Copies</span>
            <span class="detail-stat-value">{printing.totalQuantity}</span>
          </div>
          <div class="detail-stat">
            <span class="detail-stat-label">In Binders</span>
            <span class="detail-stat-value">{binderCount}</span>
          </div>
        </div>

        <!-- Binder list -->
        <div class="detail-binders">
          <h3 class="detail-binders-title">Found in</h3>
          {#each printing.binders as binder (`${binder.binderKey}|${binder.claimantUid ?? ''}`)}
            {@const binderUrl = buildBinderUrl(binder.source, binder.sourceType, binder.sourceId)}
            {@const binderColor = binderUrl ? getBinderColor(binderUrl) : '#6b7280'}
            <div class="detail-binder-row">
              <div class="detail-binder-dot" style:background={binderColor}></div>
              {#if binderUrl}
                <a
                  href={binderUrl}
                  class="detail-binder-link"
                  style:--binder-color={binderColor}
                  onclick={(e) => handleBinderClick(e, binder)}
                >
                  {binder.binderName}
                </a>
              {:else}
                <span class="detail-binder-name">{binder.binderName}</span>
              {/if}
              {#if binder.ownerName}
                <span class="detail-binder-owner">{binder.ownerName}</span>
              {/if}
              {#if binder.shared === false}<NotSharedHere />{/if}
              <span class="detail-binder-qty">x{binder.quantity}</span>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  .detail-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(44, 36, 25, 0.25);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-xl);
    animation: fade-in 0.15s ease-out;
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .detail-content {
    position: relative;
    background: var(--color-bg);
    border-radius: 18px;
    border: 1px solid var(--color-border-subtle);
    max-width: 640px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 16px 48px rgba(44, 36, 25, 0.15);
    animation: detail-slide-up 0.25s ease-out;
  }

  @keyframes detail-slide-up {
    from {
      opacity: 0;
      transform: translateY(16px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .detail-close {
    position: absolute;
    top: 18px;
    right: 18px;
    z-index: 10;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-text-tertiary);
    transition: background 0.1s;
  }

  .detail-close:hover {
    background: var(--color-accent-bg);
  }

  .detail-layout {
    display: flex;
    gap: 1.25rem;
    padding: var(--space-xl);
  }

  .detail-image-area {
    flex-shrink: 0;
    width: 220px;
  }

  .detail-card-image {
    width: 100%;
    height: auto;
    border-radius: 4%;
    display: block;
    box-shadow: 0 4px 12px rgba(40, 30, 15, 0.12);
  }

  .detail-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-card-name {
    font-family: var(--font-serif);
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0;
    line-height: 1.3;
  }

  .detail-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    font-family: var(--font-mono);
    color: var(--color-text-tertiary);
    flex-wrap: wrap;
  }

  .detail-set {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    --set-symbol-size: 1.125em;
  }

  /* Rarity colors (MTG standard — keep as-is) */
  .detail-set.rarity-common {
    color: #374151;
  }
  .detail-set.rarity-uncommon {
    color: #71717a;
  }
  .detail-set.rarity-rare {
    color: #b8860b;
  }
  .detail-set.rarity-mythic {
    color: #cc3700;
  }
  .detail-set.rarity-special {
    color: #7c3aed;
  }

  .detail-collector {
    color: var(--color-text-muted);
  }

  .detail-finish-badge {
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1px 6px;
    border-radius: 9999px;
    background: var(--color-accent-bg);
    color: var(--color-text-tertiary);
    border: 1px solid var(--color-border-subtle);
  }

  .detail-finish-badge.foil {
    background: linear-gradient(135deg, #f5ebc8 0%, #e8d5c0 50%, #d5ceb8 100%);
    color: var(--color-text-secondary);
    border-color: var(--color-border);
  }

  .detail-type-line {
    font-size: 0.8125rem;
    color: var(--color-text-secondary);
    margin: 0;
    font-style: italic;
  }

  .detail-stats {
    display: flex;
    gap: 1rem;
    padding: 0.625rem 0;
    border-top: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    margin: 0.25rem 0;
  }

  .detail-stat {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .detail-stat-label {
    font-size: 0.6875rem;
    font-weight: 500;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-family: var(--font-mono);
  }

  .detail-stat-value {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .detail-binders {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .detail-binders-title {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
    font-family: var(--font-mono);
  }

  .detail-binder-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
  }

  .detail-binder-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .detail-binder-link {
    color: var(--binder-color, var(--color-text));
    text-decoration: none;
    font-weight: 500;
    transition: opacity 0.15s;
  }

  .detail-binder-link:hover {
    opacity: 0.75;
    text-decoration: underline;
  }

  .detail-binder-name {
    color: var(--color-text);
    font-weight: 500;
  }

  .detail-binder-owner {
    color: var(--color-text-muted);
    font-size: 0.75rem;
  }

  .detail-binder-qty {
    color: var(--color-text-muted);
    font-size: 0.75rem;
    margin-left: auto;
  }

  @media (max-width: 520px) {
    .detail-layout {
      flex-direction: column;
      padding: 1rem;
    }

    .detail-image-area {
      width: 100%;
      max-width: 260px;
      margin: 0 auto;
    }

    .detail-content {
      max-height: 95vh;
    }

    .detail-backdrop {
      padding: var(--space-md);
    }
  }
</style>
